<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';

import type {
  INoneNegativeCarryoverFormComposableOptions,
} from '@modules/affiliates-approved/submodules/none-negative-carryover/composables/useNoneNegativeCarryoverForm';
import type { INoneNegativeCarryoverForm } from '@modules/affiliates-approved/submodules/none-negative-carryover/types';
import type { FormResult } from '@modules/forms/components/VForm/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import { useAffiliatesTranslate } from '@modules/affiliates-approved/composables';
import { useChangeNncoMutation } from '@modules/affiliates-approved/mutations';
import { useNoneNegativeCarryoverForm } from '@modules/affiliates-approved/submodules/none-negative-carryover/composables';
import { TRANSLATION_GROUP_AFFILIATES_NONE_NEGATIVE_CARRYOVER } from '@modules/affiliates-approved/submodules/none-negative-carryover/constants';
import { useFormatMonthDate } from '@modules/core/composables/datetime';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import VForm from '@modules/forms/components/VForm/VForm.vue';

import type { IChangeNncoDialogEmits, IChangeNncoDialogProps } from './types';

const props = withDefaults(defineProps<IChangeNncoDialogProps>(), {
  selectedAffiliates: () => [],
});

defineEmits<IChangeNncoDialogEmits>();

const visible = defineModel<boolean>('visible');

const { translate } = useAffiliatesTranslate();
const { confirmEdit } = useConfirmDialogs();
const { formatMonthDate } = useFormatMonthDate();

const formValues = ref<INoneNegativeCarryoverForm>({
  date: new Date(),
  enable_nnco: true,
});

const {
  isLoading,
  errors,
  clearError,
  mutateAsync,
} = useChangeNncoMutation();

const formOptions = computed<INoneNegativeCarryoverFormComposableOptions>(() => ({
  enableNncoValue: formValues.value.enable_nnco,
  dateValue: formValues.value.date,
}));

const { formSchema, formGroups } = useNoneNegativeCarryoverForm(formOptions);

function handleChange(
  key: keyof INoneNegativeCarryoverForm,
  value: INoneNegativeCarryoverForm[keyof INoneNegativeCarryoverForm] | undefined,
  result: FormResult<INoneNegativeCarryoverForm>,
) {
  formValues.value = result.values;
}

async function applyChangingNnco(): Promise<void> {
  if (!props.tableState || !props.selectedAffiliates)
    return;

  await mutateAsync({
    enable_nnco: formValues.value.enable_nnco,
    date: formatMonthDate(formValues.value.date),
    affiliate_ids: props.selectedAffiliates,
    segment_id: props.tableState.filter_set_id,
    page: props.tableState.page || 1,
  });

  visible.value = false;
}

function confirmChanging(event: MouseEvent): void {
  confirmEdit({
    event,
    message: translate('CONFIRM_NNCO_QUESTION'),
    acceptLabel: translate('CONFIRM_NNCO_OK'),
    rejectLabel: translate('CONFIRM_NNCO_CANCEL'),
    accept: () => {
      void applyChangingNnco();
    },
  });
}
</script>

<template>
  <VDialog
    v-model:visible="visible"
    :title="translate('CHANGE_NNCO_TITLE')"
    @hide="$emit('hide')"
  >
    <template #default>
      <VForm
        :schema="formSchema"
        :groups="formGroups"
        :custom-errors="errors"
        :is-loading="isLoading"
        @change="handleChange"
        @interacted="clearError"
      />
    </template>
    <template #footer>
      <VButton
        :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_NONE_NEGATIVE_CARRYOVER, 'SUBMIT')"
        :loading="isLoading"
        @click="confirmChanging($event)"
      />
    </template>
  </VDialog>
</template>
